import { inject, Injectable, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Observable, of, catchError, tap, lastValueFrom, map } from 'rxjs';
import { ServicioContenidoModyo } from '../modyo/modyo-contenido.service';
import { ServicioTransformadorModyo } from '../modyo/modyo-transformador.service';
import type { EstructuraPaginaModyo } from '@core/interfaces/pagina-modyo.interface';
import type { ModyoEntryResponse } from '@core/interfaces/modyo-entry-response.interface';

@Injectable({
  providedIn: 'root',
})
export class ServicioResolucionDatosSsr {
  private readonly servicioContenido = inject(ServicioContenidoModyo);
  private readonly servicioTransformador = inject(ServicioTransformadorModyo);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly esServidor = isPlatformServer(this.platformId);

  public obtenerDatosPagina(uidTipo: string, slug: string): Observable<EstructuraPaginaModyo> {
    const clave = makeStateKey<EstructuraPaginaModyo>(`pagina-${uidTipo}-${slug}`);

    if (this.esServidor) {
      return of(this.obtenerEstructuraPaginaVacia(slug));
    }

    const datosEnCache = this.transferState.get(clave, null);
    if (datosEnCache) {
      this.transferState.remove(clave);
      return of(datosEnCache);
    }

    return this.servicioContenido.obtenerEntradaPorSlug<ModyoEntryResponse>(uidTipo, slug).pipe(
      map((entryRaw) => this.servicioTransformador.transformarPagina(entryRaw)),
      tap((datos) => {
        if (this.esServidor) {
          this.transferState.set(clave, datos);
        }
      }),
      catchError(() => of(this.obtenerEstructuraPaginaVacia(slug))),
    );
  }

  public async precargarPaginas(paginas: Array<{ uidTipo: string; slug: string }>): Promise<void> {
    if (!this.esServidor) {
      return;
    }

    const promesas = paginas.map((pagina) =>
      lastValueFrom(this.obtenerDatosPagina(pagina.uidTipo, pagina.slug)).catch(() => null),
    );

    await Promise.allSettled(promesas);
  }

  public tieneDatosPaginaEnCache(uidTipo: string, slug: string): boolean {
    const clave = makeStateKey<EstructuraPaginaModyo>(`pagina-${uidTipo}-${slug}`);
    return this.transferState.hasKey(clave);
  }

  public obtenerEstructuraPaginaVacia(slug: string): EstructuraPaginaModyo {
    return {
      meta: {
        uuid: '',
        version: '1.0',
        slug,
        titulo: 'Página no encontrada',
      },
      componentes: [],
      seo: {
        titulo: 'Página no encontrada',
        descripcion: 'La página solicitada no está disponible',
      },
    };
  }
}
